import './RadiusPicker.css'
import 'leaflet/dist/leaflet.css'

import React, { useEffect, useRef, useState } from 'react'
import L, { LatLngExpression, Map } from 'leaflet'
import { Circle, MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet'

import { useAppContext } from '@/context/app'
import { defaultMapCenter } from '@/env/config'

export interface RadiusPickerValue {
  lat: number
  lng: number
  radius: number
}

interface RadiusPickerProps {
  value?: RadiusPickerValue | null
  onChange?: (value: RadiusPickerValue) => void
  defaultRadius?: number
  minRadius?: number
  maxRadius?: number
  zoom?: number
  height?: string | number
  disabled?: boolean
}

interface AddressSearchResult {
  place_id: number
  display_name: string
  lat: string
  lon: string
}

function RadiusPickerMapEvents({ disabled, onPick }: {
  disabled: boolean
  onPick: (lat: number, lng: number) => void
}) {
  useMapEvents({
    click: (event) => {
      if (disabled) {
        return
      }
      onPick(event.latlng.lat, event.latlng.lng)
    }
  })

  return null
}

function RadiusPickerViewport({ value }: {
  value?: RadiusPickerValue | null
}) {
  const map = useMap()

  useEffect(() => {
    if (!value) {
      return
    }

    const bounds = L.latLng(value.lat, value.lng).toBounds(value.radius * 2)
    map.fitBounds(bounds, { padding: [24, 24] })
  }, [map, value?.lat, value?.lng, value?.radius])

  return null
}

export const RadiusPicker = ({
  value,
  onChange,
  defaultRadius = 100,
  minRadius = 10,
  maxRadius = 100000,
  zoom = 13,
  height = '420px',
  disabled = false
}: RadiusPickerProps) => {
  const { translates } = useAppContext()
  const mapRef = useRef<Map>(null)
  const searchAbortRef = useRef<AbortController | null>(null)
  const searchTimeoutRef = useRef<number | null>(null)
  const selectedAddressRef = useRef<string | null>(null)
  const [draftRadius, setDraftRadius] = useState<string>(() => String(value?.radius ?? defaultRadius))
  const [addressQuery, setAddressQuery] = useState('')
  const [addressResults, setAddressResults] = useState<AddressSearchResult[]>([])
  const [isSearchingAddress, setIsSearchingAddress] = useState(false)
  const [addressSearchError, setAddressSearchError] = useState(false)

  useEffect(() => {
    setDraftRadius(String(value?.radius ?? defaultRadius))
  }, [defaultRadius, value?.radius])

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.invalidateSize()
    }
  }, [])

  useEffect(() => {
    const query = addressQuery.trim()

    if (searchTimeoutRef.current !== null) {
      window.clearTimeout(searchTimeoutRef.current)
    }
    searchAbortRef.current?.abort()

    if (selectedAddressRef.current === query) {
      selectedAddressRef.current = null
      return
    }

    if (query.length < 3) {
      setAddressResults([])
      setIsSearchingAddress(false)
      setAddressSearchError(false)
      return
    }

    const controller = new AbortController()
    searchAbortRef.current = controller
    setIsSearchingAddress(true)
    setAddressSearchError(false)
    searchTimeoutRef.current = window.setTimeout(() => {
      const params = new URLSearchParams({
        format: 'jsonv2',
        q: query,
        limit: '5',
        addressdetails: '0',
      })
      fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
        signal: controller.signal,
        headers: { 'Accept-Language': navigator.language },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Address search failed')
          }
          return response.json() as Promise<AddressSearchResult[]>
        })
        .then((results) => setAddressResults(results))
        .catch((error: unknown) => {
          if ((error as { name?: string }).name !== 'AbortError') {
            setAddressResults([])
            setAddressSearchError(true)
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setIsSearchingAddress(false)
          }
        })
    }, 1000)

    return () => {
      if (searchTimeoutRef.current !== null) {
        window.clearTimeout(searchTimeoutRef.current)
      }
      controller.abort()
    }
  }, [addressQuery])

  const selectedPosition = value ? [value.lat, value.lng] as [number, number] : undefined
  const center: LatLngExpression = selectedPosition || defaultMapCenter

  const markerIcon = L.divIcon({
    className: 'car-icon',
    html: '<img src="/public/img/gps/map_point.png" style="width: 20px; display: block;" />',
    iconSize: [20, 20],
    iconAnchor: [10, 10]
  })

  function emitChange(next: RadiusPickerValue) {
    onChange?.({
      lat: next.lat,
      lng: next.lng,
      radius: Math.min(maxRadius, Math.max(minRadius, next.radius))
    })
  }

  function handlePick(lat: number, lng: number) {
    const parsedDraftRadius = Number(draftRadius)

    emitChange({
      lat,
      lng,
      radius: value?.radius ?? (Number.isNaN(parsedDraftRadius) ? defaultRadius : parsedDraftRadius)
    })
  }

  function handleRadiusChange(input: string) {
    setDraftRadius(input)

    const parsedRadius = Number(input)
    if (!value || Number.isNaN(parsedRadius)) {
      return
    }

    emitChange({
      lat: value.lat,
      lng: value.lng,
      radius: parsedRadius
    })
  }

  function selectAddress(result: AddressSearchResult) {
    const lat = Number(result.lat)
    const lng = Number(result.lon)
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return
    }

    handlePick(lat, lng)
    selectedAddressRef.current = result.display_name.trim()
    setAddressQuery(result.display_name)
    setAddressResults([])
    mapRef.current?.flyTo([lat, lng], Math.max(mapRef.current.getZoom(), 15))
  }

  return (
    <div className='radius-picker'>
      <div className='radius-picker__panel'>
        <div className='radius-picker__address-search'>
          <label htmlFor='radius-picker-address'>
            {translates?.for('map.radius_picker.address_search_label', 'Szukaj adresu') || 'Szukaj adresu'}
          </label>
          <div className='radius-picker__address-search-input'>
            <input
              id='radius-picker-address'
              type='search'
              autoComplete='street-address'
              value={addressQuery}
              disabled={disabled}
              placeholder={translates?.for('map.radius_picker.address_search_placeholder', 'Wpisz adres lub miejscowość') || 'Wpisz adres lub miejscowość'}
              onChange={(event) => setAddressQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  if (addressResults[0]) {
                    selectAddress(addressResults[0])
                  }
                }
              }}
            />
            {isSearchingAddress ? <span className='radius-picker__address-search-loading' aria-label='Loading' /> : null}
          </div>
          {addressResults.length > 0 ? <ul className='radius-picker__address-results'>
            {addressResults.map((result) => <li key={result.place_id}>
              <button type='button' onClick={() => selectAddress(result)}>
                {result.display_name}
              </button>
            </li>)}
          </ul> : null}
          {addressQuery.trim().length >= 3 && !isSearchingAddress && addressResults.length === 0 ? <p className='radius-picker__address-search-status'>
            {addressSearchError
              ? (translates?.for('map.radius_picker.address_search_error', 'Nie udało się wyszukać adresu.') || 'Nie udało się wyszukać adresu.')
              : (translates?.for('map.radius_picker.address_search_empty', 'Brak wyników.') || 'Brak wyników.')}
          </p> : null}
        </div>
        <label htmlFor='radius-picker-distance'>{translates?.for('map.radius_picker.radius_label') || 'Radius (meters)'}</label>
        <input
          id='radius-picker-distance'
          type='number'
          min={minRadius}
          max={maxRadius}
          step='10'
          value={draftRadius}
          disabled={disabled}
          onChange={(event) => handleRadiusChange(event.target.value)}
        />
        <p className='radius-picker__hint'>{translates?.for('map.radius_picker.hint') || 'Click on the map to set the center point.'}</p>
        {value ? <p className='radius-picker__coords'>
          {(translates?.for('map.radius_picker.coordinates') || '{lat}, {lng}')
            .replace('{lat}', value.lat.toFixed(6))
            .replace('{lng}', value.lng.toFixed(6))}
        </p> : null}
      </div>

      <MapContainer
        attributionControl={false}
        ref={mapRef}
        center={center}
        zoom={zoom}
        style={{ height, width: '100%' }}
        className='radius-picker__map'
      >
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
        />

        <RadiusPickerMapEvents disabled={disabled} onPick={handlePick} />
        <RadiusPickerViewport value={value} />

        {selectedPosition ? <>
          <Marker position={selectedPosition} icon={markerIcon} />
          <Circle
            center={selectedPosition}
            radius={value?.radius ?? defaultRadius}
            pathOptions={{
              color: '#0b6c00',
              fillColor: '#28a745',
              fillOpacity: 0.18,
              weight: 2
            }}
          />
        </> : null}
      </MapContainer>
    </div>
  )
}

export default RadiusPicker
